import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Copy, Send, Trash2, Server } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/hosting/clients`;
const GOLD = "#f5a623";
const NAVY_DEEP = "#131c33";
const STATUS_COLOR = { invited: GOLD, active: "#4ade80", canceled: "#ff8a8a", past_due: "#ff8a8a" };

const box = { background: NAVY_DEEP, border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 24, marginTop: 24 };
const input = { background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, color: "#fff", padding: "10px 12px", fontSize: 14 };
const btn = { background: GOLD, color: "#1a2744", border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.06em", cursor: "pointer" };
const iconBtn = { background: "transparent", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, color: "#fff", padding: 6, cursor: "pointer" };
const fmt = (iso) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric", timeZone: "UTC" });

export const HostingClientsPanel = ({ password }) => {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", go_live_date: "" });
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const headers = { "x-stats-password": password };

  const load = useCallback(() => {
    axios.get(API, { headers: { "x-stats-password": password } }).then(({ data }) => setClients(data)).catch(() => {});
  }, [password]);
  useEffect(load, [load]);

  const linkFor = (c) => `${window.location.origin}/hosting/setup/${c.token}`;
  const run = async (fn, ok) => {
    setBusy(true);
    setMsg(null);
    try {
      const res = await fn();
      setMsg(ok(res?.data));
      load();
    } catch (e) {
      setMsg(e.response?.data?.detail?.toString() || "Something went wrong");
    }
    setBusy(false);
  };

  const add = (e) => {
    e.preventDefault();
    run(
      () => axios.post(API, { ...form, send_invite: true }, { headers }),
      (d) => {
        setForm({ name: "", email: "", go_live_date: "" });
        return d.invite_sent ? `Invite emailed to ${d.email}` : "Client saved — invite email failed, copy the link instead";
      }
    );
  };

  return (
    <section style={box} data-testid="hosting-panel">
      <h3 style={{ margin: 0, fontFamily: '"Roboto Condensed", sans-serif', fontSize: 18, color: "#fff", display: "flex", gap: 8, alignItems: "center" }}>
        <Server size={18} color={GOLD} /> Front Desk Hosting Clients
      </h3>
      <p style={{ color: "rgba(255,255,255,0.55)", fontSize: 13, margin: "6px 0 16px" }}>
        Free first year from go-live, then $75/mo. Adding a client emails them a private billing setup link. Reminders go out 30 days before the free year ends.
      </p>
      <form onSubmit={add} style={{ display: "flex", flexWrap: "wrap", gap: 10 }} data-testid="hosting-add-form">
        <input required placeholder="Client / business name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} style={{ ...input, flex: "1 1 180px" }} data-testid="hosting-add-name" />
        <input required type="email" placeholder="Billing email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} style={{ ...input, flex: "1 1 200px" }} data-testid="hosting-add-email" />
        <input required type="date" title="Go-live date" value={form.go_live_date} onChange={(e) => setForm({ ...form, go_live_date: e.target.value })} style={input} data-testid="hosting-add-date" />
        <button type="submit" disabled={busy} style={{ ...btn, opacity: busy ? 0.6 : 1 }} data-testid="hosting-add-submit">Add & send invite</button>
      </form>
      {msg && <p style={{ color: GOLD, fontSize: 13, marginTop: 10 }} data-testid="hosting-msg">{msg}</p>}
      <ul style={{ listStyle: "none", padding: 0, margin: "18px 0 0" }} data-testid="hosting-client-list">
        {clients.length === 0 && <li style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>No hosting clients yet.</li>}
        {clients.map((c) => (
          <li key={c.id} style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "center", padding: "10px 0", borderTop: "1px solid rgba(255,255,255,0.06)", fontSize: 13 }} data-testid={`hosting-client-${c.id}`}>
            <span style={{ color: "#fff", fontWeight: 700, minWidth: 160 }}>{c.name}</span>
            <span style={{ color: "rgba(255,255,255,0.6)", minWidth: 180 }}>{c.email}</span>
            <span style={{ color: "rgba(255,255,255,0.6)" }}>Free until {fmt(c.trial_end)}</span>
            <span style={{ color: STATUS_COLOR[c.status], textTransform: "uppercase", fontSize: 11, letterSpacing: "0.1em" }} data-testid={`hosting-status-${c.id}`}>
              {{ invited: "Awaiting card", past_due: "Payment failing" }[c.status] || c.status}
            </span>
            {c.reminder_sent_at && <span style={{ color: "rgba(255,255,255,0.4)", fontSize: 11 }}>Reminder sent</span>}
            <span style={{ marginLeft: "auto", display: "flex", gap: 6 }}>
              {c.status !== "active" && (
                <>
                  <button type="button" title="Copy setup link" style={iconBtn} onClick={() => navigator.clipboard?.writeText(linkFor(c)).then(() => setMsg("Setup link copied"))} data-testid={`hosting-copy-${c.id}`}><Copy size={14} /></button>
                  <button type="button" title="Resend invite" disabled={busy} style={iconBtn} onClick={() => run(() => axios.post(`${API}/${c.id}/resend`, {}, { headers }), (d) => (d.invite_sent ? "Invite re-sent" : "Email failed"))} data-testid={`hosting-resend-${c.id}`}><Send size={14} /></button>
                  <button type="button" title="Remove" disabled={busy} style={iconBtn} onClick={() => window.confirm(`Remove ${c.name}?`) && run(() => axios.delete(`${API}/${c.id}`, { headers }), () => "Client removed")} data-testid={`hosting-delete-${c.id}`}><Trash2 size={14} /></button>
                </>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
};
